import { Button, Checkbox, Divider, FormControlLabel, Grid, InputAdornment, Link, Stack, TextField, Tooltip, Typography } from '@vapor/material'
import { AutocompleteTextField, FormItem, MultiContactInput } from 'hic-components'
import { useTranslation } from 'react-i18next'
import { CervedSubjectFragment, CustomerDetailFragment, MailContactType, PhoneContactType, WebContactType, useValidateItalianVatCodeLazyQuery } from 'src/api/api.generated'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { displayMailContactType, displayPhoneContactType, displayWebContactType } from 'src/utils/contact'
import { CustomerFormData, FocusedField } from '../customer.form.types'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import * as z from 'zod'
import { Controller } from 'react-hook-form'
import useCompanyForm from './company.form.hook'
import { Language, languageList, localizedLanguageKeys } from 'src/api/language'
import { useCallback, useMemo, useState } from 'react'
import { CompanyNameInput } from './company-name-input'
import CervedResultsDialog, { CervedSearchFilter } from 'src/app/components/cerved-results/cerved-results'
import AddressInput from 'src/app/components/adress-input/address-input'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useFetchAddress from 'src/app/hooks/fetch-address/use-fetch-address'
import TagInput from 'src/app/components/tag-input/tag-input'
import { FormLayoutAppearance } from 'src/app/layout/form-layout/form-layout.types'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import PrivacyConsentCheckbox from 'src/app/components/privacy/privacy-consent-checkbox'
import CreditSingleSelect from 'src/app/components/form-fields/credit-single-select'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { HiCIcon } from 'src/app/components/icons/icon'
import { faInfoCircle, faWandMagicSparkles } from '@fortawesome/pro-regular-svg-icons'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import UnsavedChangesTypography from 'src/app/components/unsaved-changes/unsaved-changes-typography/unsaved-changes-typography'
import UnsavedChangesDialog from 'src/app/components/unsaved-changes/unsaved-changes-dialog/unsaved-changes-dialog'
import FiscalCodeInput from '../components/fiscal-code-input'
import VatsSelect from '../components/vats-select'
import HistoricalCustomerResearchGrid from 'src/app/components/form-fields/historical-customer-research/historical-customer-research.grid'
import { useProperty, useTenant } from 'src/app/hooks/context/context.hook'
import CustomerTabs from '../components/customer-tabs'

export type CompanyFormProps = {
  appearance: FormLayoutAppearance
  title: string
  loading: boolean
  customer: CustomerDetailFragment | null
  onSubmit: (data: CustomerFormData) => Promise<void>
  onCancel: () => void
  focusedField?: FocusedField
}

export default function CompanyForm(props: CompanyFormProps) {
  const { appearance, title, loading, customer, onSubmit, onCancel, focusedField } = props
  const formatDate = useFormatDate()
  const { t, i18n } = useTranslation()
  const { md } = useBreakpoints()
  const isDialog = appearance.type === 'DIALOG' || appearance.type === 'DRAWER'
  const property = useProperty()
  const tenant = useTenant()
  const contactColumns = useMemo(() => (md && !isDialog ? '2' : '1'), [md, isDialog])
  const fetchAddress = useFetchAddress()
  const { companyForm, onFormSubmit, control, errors, setCervedCustomer, formValue, isDirty } = useCompanyForm({ onSubmit, customer })
  const privacyLog = customer?.privacy?.log
  const [checkVatCode] = useValidateItalianVatCodeLazyQuery()
  const showError = useErrorSnackBar()
  const showSuccess = useSuccessSnackBar()
  const [tab, setTab] = useState(0)
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false)

  type CompanyForm = z.infer<typeof companyForm>

  const onCervedSubjectSelected = useCallback(
    async (subject: CervedSubjectFragment) => {
      const address = await fetchAddress({
        address: subject.address?.street?.description,
        city: subject.address?.city?.description,
        countryIsoCode: subject.address?.country?.isoCode,
        municipalityIstat: subject.address?.city?.istat_code,
        municipalityName: subject.address?.city?.description,
        municipalityProvince: subject.address?.province?.code,
        postalCode: subject.address?.postal_code,
      })
      setCervedCustomer(subject, address)
    },
    [setCervedCustomer, fetchAddress],
  )

  const [cervedSearchFilter, setCervedSearchFilter] = useState<CervedSearchFilter | undefined>(undefined)

  const onRefChange = useCallback(
    (node: HTMLDivElement | null, field: FocusedField) => {
      if (!focusedField) {
        return
      }
      if (node && field === focusedField) {
        node.scrollIntoView()
      }
    },
    [focusedField],
  )

  function onClickOnCancel() {
    if (isDirty) {
      setShowUnsavedChangesDialog(true)
    } else {
      onCancel()
    }
  }

  return (
    <>
      <CervedResultsDialog //
        filter={cervedSearchFilter}
        onClose={() => setCervedSearchFilter(undefined)}
        onCervedSubjectSelected={onCervedSubjectSelected}
      />
      <FormLayout
        appearance={
          'onClose' in appearance
            ? {
                ...appearance,
                onClose: onClickOnCancel,
              }
            : appearance
        }
        title={title}
        toolbar={
          <FormLayoutDefaultToolbar
            saveButton={{
              loading: loading,
              disabled: loading,
              clickHandler: onFormSubmit,
            }}
            cancelButton={{
              disabled: loading,
              clickHandler: onClickOnCancel,
              clickConfirmDialog: (
                <UnsavedChangesDialog
                  open={showUnsavedChangesDialog}
                  handleCloseDialog={() => {
                    setShowUnsavedChangesDialog(false)
                  }}
                  onCloseAndDiscard={onCancel}
                />
              ),
            }}
            leftItem={<UnsavedChangesTypography show={isDirty} />}
          />
        }
      >
        <form onSubmit={onFormSubmit}>
          <Grid container spacing={2}>
            {customer ? (
              <Grid item xs={12}>
                <CustomerTabs value={tab} onChange={setTab} disabled={loading} />
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <Typography variant="titleSmall">{t('pms.common.customer.generic_data_subtitle')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider light />
                </Grid>
              </>
            )}
            {customer && tab === 1 ? (
              <>
                <Grid item xs={12} width={800}>
                  <HistoricalCustomerResearchGrid propertyId={property?.id ?? ''} tenantId={tenant.id} initialCustomer={customer} hideCustomerFilter />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <Controller<CompanyForm, 'companyName'>
                    name={'companyName'}
                    control={control}
                    render={({ field }) => (
                      <CompanyNameInput //
                        onCervedSearchClick={(searchText) => setCervedSearchFilter({ type: 'SEARCH_TEXT', searchText: searchText })}
                        error={errors.companyName}
                        value={field.value}
                        disabled={loading}
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        physicalPersonLabel={t('pms.common.customer.physical_person')}
                        lastNameLabel={t('pms.common.customer.lastName')}
                        firstNameLabel={t('pms.common.customer.firstName')}
                        nameLabel={t('pms.common.customer.company_name')}
                        ref={field.ref}
                        size={'small'}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller<CompanyForm, 'vatNumberFiscalCode.vatNumber'>
                    name={'vatNumberFiscalCode.vatNumber'}
                    control={control}
                    render={({ field: { value, onChange, ref } }) => (
                      <div ref={(node) => onRefChange(node, 'VAT_NUMBER')}>
                        <FormItem //
                          fullWidth
                          fieldLabel={t('pms.common.customer.vat_number')}
                          errorMessage={errors.vatNumberFiscalCode?.vatNumber?.message}
                          fieldEndIcon={
                            <Link
                              onClick={async () => {
                                if (!value) {
                                  return showError(t('pms.common.customer.missing_fiscal_code'))
                                }

                                const { data, error } = await checkVatCode({ variables: { vatCode: value } })

                                if (error) {
                                  return showError(error)
                                }

                                if (data?.validateItalianVatCode) {
                                  showSuccess(t('pms.common.customer.valid_vat_code'))
                                } else {
                                  showError(t('pms.common.customer.invalid_vat_code'))
                                }
                              }}
                            >
                              {t('pms.common.customer.fiscal_code_check')}
                            </Link>
                          }
                        >
                          <TextField //
                            disabled={loading}
                            fullWidth
                            value={value || ''}
                            onBlur={(event) => {
                              onChange(maskVatNumber(event.target.value ?? ''))
                            }}
                            onChange={(event) => {
                              onChange(maskVatNumber(event.target.value ?? ''))
                            }}
                            error={errors.vatNumberFiscalCode?.vatNumber ? true : false}
                            inputRef={ref}
                            size={'small'}
                            sx={{
                              '& svg': {
                                color: 'unset!important',
                              },
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Button //
                                    onClick={() => {
                                      if (!value) {
                                        return showError(t('pms.common.customer.missing_vat_number'))
                                      }
                                      setCervedSearchFilter({ type: 'VAT_NUMBER', vatNumber: value })
                                    }}
                                    size={'small'}
                                    disabled={loading}
                                    style={{ minWidth: 0, minHeight: 0 }}
                                  >
                                    <FontAwesomeIcon icon={faWandMagicSparkles} />
                                  </Button>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </FormItem>
                      </div>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller<CompanyForm, 'vatNumberFiscalCode.fiscalCode'>
                    name={'vatNumberFiscalCode.fiscalCode'}
                    control={control}
                    render={({ field }) => (
                      <FiscalCodeInput
                        value={field.value || ''}
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        errorMessage={errors?.vatNumberFiscalCode?.fiscalCode?.message}
                        disabled={loading}
                        customer={formValue}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller<CompanyForm, 'sdiCode'>
                    name={'sdiCode'}
                    control={control}
                    render={({ field }) => (
                      <FormItem //
                        fullWidth
                        fieldLabel={t('pms.common.customer.sdi_code')}
                        errorMessage={errors.sdiCode?.message}
                      >
                        <TextField //
                          {...field}
                          disabled={loading}
                          fullWidth
                          value={field.value || ''}
                          onChange={(event) => {
                            field.onChange(event)
                          }}
                          error={errors.sdiCode ? true : false}
                          inputRef={field.ref}
                          size={'small'}
                        />
                      </FormItem>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="titleSmall">{t('pms.common.customer.address_subtitle')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider light />
                </Grid>
                <Grid item xs={12}>
                  <Controller<CompanyForm, 'address'> //
                    name={'address'}
                    control={control}
                    render={({ field }) => (
                      <div ref={(node) => onRefChange(node, 'ADDRESS')}>
                        <AddressInput //
                          disabled={loading}
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value)
                          }}
                          fieldsSmall
                        />
                      </div>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="titleSmall">{t('pms.common.customer.contacts_sub_title')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider light />
                </Grid>
                <Grid item xs={12}>
                  <Controller<CompanyForm, 'mailContacts'>
                    name={'mailContacts'}
                    control={control}
                    render={({ field }) => {
                      return (
                        <div
                          ref={(node) => {
                            onRefChange(node, 'EMAIL')
                          }}
                        >
                          <MultiContactInput<MailContactType> //
                            value={field.value || []}
                            onChange={(value) => {
                              field.onChange(value)
                            }}
                            error={errors?.mailContacts}
                            getTypeId={(type: MailContactType) => type}
                            displayType={(type: MailContactType) => displayMailContactType(t, type)}
                            types={Object.values(MailContactType)}
                            defaultType={MailContactType.MAIN}
                            columns={contactColumns}
                            label={t('pms.common.customer.mail')}
                            addLabel={t('commons.add')}
                            disabled={loading}
                          />
                        </div>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller<CompanyForm, 'phoneContacts'>
                    name={'phoneContacts'}
                    control={control}
                    render={({ field }) => (
                      <MultiContactInput<PhoneContactType> //
                        value={field.value || []}
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        error={errors?.phoneContacts}
                        getTypeId={(type: PhoneContactType) => type}
                        displayType={(type: PhoneContactType) => displayPhoneContactType(t, type)}
                        types={Object.values(PhoneContactType)}
                        columns={contactColumns}
                        label={t('pms.common.customer.phone')}
                        addLabel={t('commons.add')}
                        disabled={loading}
                        defaultType={PhoneContactType.MAIN}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller<CompanyForm, 'webContacts'>
                    name={'webContacts'}
                    control={control}
                    render={({ field }) => (
                      <MultiContactInput<WebContactType> //
                        value={field.value || []}
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        error={errors?.webContacts}
                        getTypeId={(type: WebContactType) => type}
                        displayType={(type: WebContactType) => displayWebContactType(t, type)}
                        types={Object.values(WebContactType)}
                        defaultType={WebContactType.WEBSITE}
                        columns={contactColumns}
                        label={t('pms.common.customer.web_contact')}
                        addLabel={t('commons.add')}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={isDialog ? 12 : 6}>
                  <Controller<CompanyForm, 'language'>
                    name={'language'}
                    control={control}
                    render={({ field }) => (
                      <FormItem //
                        fieldLabel={`${t('pms.common.customer.contact_language')}*`}
                        errorMessage={errors.language?.message}
                        sx={{ width: '50%' }}
                      >
                        <AutocompleteTextField<Language> //
                          getOptionLabel={(option: Language) => `${languageList[option].nativeName} (${languageList[option].name})`}
                          options={[...localizedLanguageKeys]}
                          value={field.value || null}
                          onChange={(value) => {
                            field.onChange(value)
                          }}
                          error={errors.language ? true : false}
                          disabled={loading}
                          inputRef={field.ref}
                          size="small"
                        />
                      </FormItem>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="titleSmall">{t('pms.common.customer.fiscal_configuration')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider light />
                </Grid>
                <Grid item xs={4}>
                  <Controller<CompanyForm, 'credit'> //
                    name={'credit'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormItem fieldLabel={t('pms.booking.reservation_new.options.credit_label')} fullWidth>
                        <CreditSingleSelect
                          value={value}
                          onChange={(event) => {
                            onChange(event)
                          }}
                        />
                      </FormItem>
                    )}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller<CompanyForm, 'stayVatId'> //
                    name={'stayVatId'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <VatsSelect
                        value={value ?? ''}
                        onChange={(newVat) => {
                          onChange(newVat)
                        }}
                        disabled={loading}
                        errorMessage={errors.stayVatId?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller<CompanyForm, 'splitPayment'> //
                    name={'splitPayment'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormItem>
                        <FormControlLabel //
                          sx={{ alignItems: 'center' }}
                          control={
                            <Checkbox
                              sx={{ paddingY: 0 }}
                              checked={value}
                              onChange={(event, checked) => {
                                onChange(event, checked)
                              }}
                            />
                          }
                          label={<Typography variant="body">{t('pms.booking.reservation_new.options.split_payment_label')}</Typography>}
                        />
                      </FormItem>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller<CompanyForm, 'riferimentoAmministrazione'> //
                    name={'riferimentoAmministrazione'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormItem>
                        <FormControlLabel //
                          sx={{ alignItems: 'center' }}
                          control={
                            <Checkbox
                              sx={{ paddingY: 0 }}
                              checked={value}
                              onChange={(event, checked) => {
                                onChange(event, checked)
                              }}
                            />
                          }
                          label={<Typography variant="body">{t('pms.common.customer.riferimento_amministrazione')}</Typography>}
                        />
                      </FormItem>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="titleSmall">{t('pms.common.customer.tag')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider light />
                </Grid>
                <Grid item xs={12}>
                  <Controller<CompanyForm, 'tagsId'> //
                    name={'tagsId'}
                    control={control}
                    render={({ field }) => (
                      <TagInput //
                        value={field.value || []}
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        size={'small'}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} display={'flex'} alignItems={'center'} columnGap={0.5}>
                  <Typography variant="titleSmall">{t('pms.common.customer.privacy_subtitle')}</Typography>
                  {privacyLog && (
                    <Tooltip
                      title={
                        <span style={{ whiteSpace: 'pre-line' }}>
                          {t('pms.common.customer.privacy_subtitle_tooltip', {
                            timestamp: privacyLog?.timestamp ? formatDate(privacyLog?.timestamp, 'Pp') : '',
                            device: privacyLog.platform ?? '',
                            ip: privacyLog.ip ?? '',
                            browser: privacyLog.userAgent ?? '',
                          })}
                        </span>
                      }
                    >
                      <span>
                        <HiCIcon>
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </HiCIcon>
                      </span>
                    </Tooltip>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider light />
                </Grid>
                <Grid item xs={12} md={isDialog ? 12 : 4}>
                  <Stack>
                    <Controller<CompanyForm, 'privacyManagement'> //
                      name={'privacyManagement'}
                      control={control}
                      render={({ field }) => (
                        <PrivacyConsentCheckbox //
                          value={field.value || { consent: false }}
                          onChange={(value) => {
                            field.onChange(value)
                          }}
                          label={t('pms.common.customer.privacy_management')}
                          i18n={i18n}
                          disabled={loading}
                        />
                      )}
                    />
                    <Controller<CompanyForm, 'privacyMarketing'> //
                      name={'privacyMarketing'}
                      control={control}
                      render={({ field }) => (
                        <PrivacyConsentCheckbox //
                          value={field.value || { consent: false }}
                          onChange={(value) => {
                            field.onChange(value)
                          }}
                          label={t('pms.common.customer.privacy_marketing')}
                          i18n={i18n}
                          disabled={loading}
                        />
                      )}
                    />
                    <Controller<CompanyForm, 'privacyDataThirdParty'> //
                      name={'privacyDataThirdParty'}
                      control={control}
                      render={({ field }) => (
                        <PrivacyConsentCheckbox //
                          value={field.value || { consent: false }}
                          onChange={(value) => {
                            field.onChange(value)
                          }}
                          label={t('pms.common.customer.privacy_data_third_party')}
                          i18n={i18n}
                          disabled={loading}
                        />
                      )}
                    />
                    <Controller<CompanyForm, 'privacyProfiledMarketing'> //
                      name={'privacyProfiledMarketing'}
                      control={control}
                      render={({ field }) => (
                        <PrivacyConsentCheckbox //
                          value={field.value || { consent: false }}
                          onChange={(value) => {
                            field.onChange(value)
                          }}
                          label={t('pms.common.customer.privacy_profiled_marketing')}
                          i18n={i18n}
                          disabled={loading}
                        />
                      )}
                    />
                  </Stack>
                </Grid>
              </>
            )}
          </Grid>
        </form>
      </FormLayout>
    </>
  )
}

const VAT_REGEX = /^[A-Za-z]{2}\d{11}$/
function maskVatNumber(vat: string) {
  if (VAT_REGEX.test(vat) && vat.startsWith('IT')) {
    vat = vat.replace('IT', '')
  }

  return vat.toLocaleUpperCase()
}
